import { inject, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  distinctUntilChanged,
  from,
  map,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { HttpTranslationLoaderService } from './loaders/http-translation-loader.service';
import { Locale } from '../../enums/locale.enum';
import { CompilerService } from './compilers/compiler.service';

type FlattenedTranslations = Record<string, string>;

interface Translator {
  locale: Locale;
  translations: FlattenedTranslations;
}

export interface Translations {
  [key: string]: Translations | string;
}

export type TranslationValues = Record<string, TranslationValue>;

export type TranslationValue = string | number | boolean | null | undefined;

@Injectable({
  providedIn: 'root',
})
export class TranslateService {
  private readonly _compiler: CompilerService = inject(CompilerService);
  private readonly _loader: HttpTranslationLoaderService = inject(
    HttpTranslationLoaderService
  );

  private readonly _instant: Map<Locale, Translator>;
  private readonly _translators: Map<Locale, Promise<Translator>>;
  private readonly _locale: BehaviorSubject<Locale> =
    new BehaviorSubject<Locale>(Locale.fr);

  public constructor() {
    this._instant = new Map<Locale, Translator>();
    this._translators = new Map<Locale, Promise<Translator>>();
  }

  /**
   * Bootstrap (load the current locale and the default one)
   */
  public bootstrap(): Promise<void> {
    this._locale.subscribe((locale: Locale) => {
      this.load(locale);
    });

    const locales: Locale[] = [Locale.fr];

    return Promise.all(locales.map((locale: Locale) => this.load(locale))).then(
      () => undefined as void
    );
  }

  /**
   * Translation loading (can be overrided by injecting another TranslationLoaderService service)
   */
  private load(locale: Locale): Promise<Translator> {
    if (!this._translators.has(locale)) {
      this._translators.set(
        locale,
        new Promise<Translator>((resolve: (value: Translator) => void) => {
          this._loader
            .load(locale)
            .pipe(
              catchError((err: unknown) => {
                console.error('Translation file load issue');
                console.error(err);
                return of({});
              }),
              map(
                (data: Translations | null): Translator => ({
                  locale,
                  translations: this.flatten(data ?? {}),
                })
              ),
              tap((translator: Translator) =>
                this._instant.set(locale, translator)
              )
            )
            .subscribe((translator: Translator) => resolve(translator));
        })
      );
    }

    return this._translators.get(locale) as Promise<Translator>;
  }

  /**
   * Translations observable by locale
   */
  private translator$(locale: Locale): Observable<Translator> {
    if (!this._translators.has(locale)) {
      throw new Error(`Locale ${locale} is not loaded or supported`);
    }
    return from(this._translators.get(locale) as Promise<Translator>);
  }

  /**
   * Returns translation from key (default to default locale if the translation does not exists)
   */
  public get(key: string, values?: TranslationValues): Observable<string> {
    return this._locale.pipe(
      distinctUntilChanged(),
      switchMap((locale: Locale) => this.translator$(locale)),
      switchMap((translator: Translator) => {
        if (
          translator.translations?.[this.clean(key)] ||
          translator.locale === Locale.fr
        ) {
          return of(translator);
        } else {
          return this.translator$(Locale.fr);
        }
      }),
      map((translator: Translator) => {
        return this._compiler.exec(
          translator.locale,
          translator.translations?.[this.clean(key)] ?? key,
          values
        );
      })
    );
  }

  /**
   * Instant translation method
   */
  public instant(key: string, values?: TranslationValues): string {
    let translator: Translator | undefined = this._instant.get(
      this._locale.value
    );
    if (
      !translator?.translations?.[this.clean(key)] &&
      translator?.locale !== Locale.fr
    ) {
      translator = this._instant.get(Locale.fr);
    }

    return translator
      ? this._compiler.exec(
          translator.locale,
          translator.translations?.[this.clean(key)] ?? key,
          values
        )
      : '';
  }

  public has(key: string): boolean {
    let translator: Translator | undefined = this._instant.get(
      this._locale.value
    );

    let has: boolean = !!translator?.translations?.[this.clean(key)];
    if (!has && translator?.locale !== Locale.fr) {
      translator = this._instant.get(Locale.fr);
      has = !!translator?.translations?.[this.clean(key)];
    }

    return has;
  }

  /**
   * Transform the json translation to a flat key/value object
   */
  private flatten(data: Translations): FlattenedTranslations {
    const result: FlattenedTranslations = {};
    data = data ?? {};
    Object.keys(data).forEach((key: string) => {
      key = this.clean(key);
      const value: Translations | string = data[key];
      if (typeof value === 'object') {
        const flatObject: FlattenedTranslations = this.flatten(value);
        Object.keys(flatObject).forEach((subKey: string) => {
          result[`${key}.${subKey}`] = flatObject[subKey];
        });
      } else {
        result[key] = value;
      }
    });
    return result;
  }

  /**
   * Clean up a key
   */
  private clean(key: string): string {
    return key.replaceAll(/\s+/g, ' ').trim();
  }
}
